import { Router } from "@vaadin/router";

import { useRouter } from "@/router";

/**
 * Get the return to URL from the query params.
 * @param queryParams - The query params to add to the URL.
 * @returns The return to URL.
 */
export function getReturnTo(queryParams?: Record<string, string>): string {
  const currentLocation = window.location.pathname;
  const qs = new URLSearchParams(window.location.search);
  if (queryParams) {
    Object.entries(queryParams).forEach(([key, value]) => {
      qs.append(key, value);
    });
  }

  const hasQSParams = qs.size > 0;
  return encodeURIComponent(`${currentLocation}${hasQSParams ? "?" + qs.toString() : ""}`);
}

/**
 * Get a value from the query string.
 * @param key - The key to get the value for.
 * @param remove - Whether to remove the key from the query string.
 * @returns The value from the query string.
 */
export function getFromQueryString<T = string>(key: string, remove: boolean = false): T | undefined {
  try {
    const qs = new URLSearchParams(window.location.search);
    const value = qs.get(key);

    if (remove && value) {
      const router = useRouter();

      qs.delete(key);
      const searchString = qs.toString();

      const newUrl = new URL(window.location.href);
      newUrl.search = searchString ? `?${searchString}` : "?";

      if (router) {
        router.location.search = searchString;
        Router.go({
          pathname: window.location.pathname,
          search: newUrl.search,
        });
      }

      window.history.pushState(null, "", newUrl);
    }

    return (value as T) ?? undefined;
  } catch (error) {
    return undefined;
  }
}

/**
 * Add a key/value pair to the query string.
 * @param key - The key to add to the query string.
 * @param value - The value to add to the query string.
 */
export function addToQueryString(key: string, value: string): void {
  const url = new URL(window.location.href);
  url.searchParams.set(key, value);
  window.history.pushState(null, "", url);
}

/**
 * Validate a URL.
 * @param string - The URL to validate.
 * @returns Whether the URL is valid.
 */
export function isValidURL(string: string): boolean {
  try {
    const trimmed = string.trim();
    const regex =
      "^(?:http(s)?:\\/\\/)?(?:localhost|[\\w.-]+(?:\\.[\\w\\.-]+)+)(:[0-9]{1,5})?[\\w\\-\\._~:/?#[\\]@!\\$&'\\(\\)\\*\\+,;=.]*$";
    const pattern = new RegExp(regex);
    return pattern.test(trimmed);
  } catch (e) {
    console.log(e);
    return false;
  }
}
